import Vue from 'vue';
import VueRouter from 'vue-router';
import { OktaAuth } from '@okta/okta-auth-js';
import { getDefaultAuthData, oktaAuthConfig } from './store/store.constant';
import axios from 'axios';
import { setUserId, gaDataLayerEvent, gaPageViewEvent } from './plugins/analytics';
import { routerNames } from '@/components/appCommon.constants';
import store from './store/store';
import { storeAction } from './store/storeActions.constant';
import { storeMutation } from './store/storeMutations.constant';
import { storeGetters } from './store/storeGetters.constant';
import { appConstant, routableComponents } from "@/app.constant";
import { addDays } from "@/common/utils/dateTime";
import { setPostLoginUrl, getNextRouteFromUrl } from "./app.utils";
import { getHbRedirectByCommunityId, routesToNavigateToHivebrite } from "@/common/utils/hivebriteUtils";
import { gtmEventName } from "@/plugins/gtmAnalytics";
import { capitalizedString } from "@/common/utils/utils";
import { getToken, getMessaging } from 'firebase/messaging';
import { datadogRum } from "@datadog/browser-rum";

Vue.use(VueRouter);

if (!window.location.hash.includes(routerNames.LOGIN) && store) {
    store.state.auth.postLoginRedirectRoute = window.location.href;
}


const authClient = new OktaAuth(oktaAuthConfig);
const { paymentRedirectUrl } = store.getters;

const redirectFromConnect = (redirectUrl, next) => {
    store.dispatch(`auth/${storeAction.registerInAdobe}`)
        .then((res) => {
            if (res && res.data) {
                window.open(redirectUrl, '_self');
            } else {
                store.commit(`ypoError/${storeMutation.setYpoErrorData}`, 1, { root: true });
                next('/errorPage');
            }
        });
};

const isBaseLandingUrl = (url) => {
    return url === `${location.origin}/#/`;
};

const getCurrentUserId = (token) => {
    let result = null;
    const impersonationKey = sessionStorage.getItem(appConstant.storageKeys.impersonationKey);
    if (impersonationKey) {
        axios.defaults.headers.common['X-Connecting-As'] = impersonationKey;
        result = impersonationKey;
    } else {
        delete axios.defaults.headers.common['X-Connecting-As'];
        const accessTokenData = token && token.accessToken ? authClient.token.decode(token.accessToken) : null;
        if (accessTokenData && accessTokenData.payload && accessTokenData.payload.ypoId) {
            result = accessTokenData.payload.ypoId;
        }
    }
    return result;
};

const rewriteProfileLocations = () => {
    if (window.location.pathname.includes('portal/profile')) {
        window.location.replace(`/#/memberProfile${window.location.search}`);
    } else {
        //window.location = store.state.salesforceURL;
        window.location = `https://psconnect.ypowpo.org${window.location.pathname}`;
    }
};

// TODO : check this flow
const handleSpeakerPageNavigation = async (userId, next) => {
    return new Promise((resolve) => {
        const speakerRegistrationStatus = store.getters[`speaker/${storeGetters.speakerRegistrationStatus}`];
        if (!userId) {
            next();
            return resolve();
        }
        if (speakerRegistrationStatus === null) {
            store
                .dispatch(`speaker/${storeAction.getSpeakerRegistrationStatus}`, userId)
                .then((response) => {
                    store.commit(`speaker/${storeMutation.setSpeakerRegistrationStatus}`, response.data);
                    const status = response.data;
                    if (status === 'A' || status === 'P') {
                        next('/home');
                        return resolve();
                    }
                    next();
                    return resolve();
                })
                .catch(() => {
                    next('/home');
                    return resolve();
                });
        } else {
            if (speakerRegistrationStatus === 'A' || speakerRegistrationStatus === 'P') {
                next('/home');
                return resolve();
            }
            next();
            return resolve();
        }
    });
};

const handleYpoEventsNavigation = async (next) => {
    return new Promise((resolve) => {
        store.dispatch(`ypoEvents/${storeAction.getUnfilteredYpoEvents}`)
            .finally((response) => {
                next();
                return resolve(response);
            });
    });
};

const handleEventDirectoryNavigation = async (userGuid, next) => {
    return new Promise((resolve) => {
        store.commit(`ypoEvents/${storeMutation.setYpoEventsSort}`);
        next(`/events`);
        resolve();
    });
};

const memberProfileUrlRedirection = (isMyProfileRedirect = false) => {
    authClient.tokenManager.get('accessToken').then((token) => {
        if (getCurrentUserId(token)) {
            let replaceUrl = `/#/memberProfile?memberId=${getCurrentUserId(token).toLowerCase()}&mode=edit`;
            if (isMyProfileRedirect) {
                gaDataLayerEvent(gtmEventName["C_Connect_Profile_Edit_Redirect_From_C+"], getCurrentUserId(token));
                replaceUrl = `${replaceUrl}&connectPlusRedirect=true`;
            }
            window.location.replace(replaceUrl);
        }
    });
};

const initiateOktaFlow = (authData) => {
    let newAuthData = authData && Object.keys(authData).length ? { ...authData } : {};
    axios.defaults.headers.common.Authorization = '';
    return new Promise((resolve) => {
        authClient.tokenManager.get('accessToken')
            .then(async (res) => {
                if (!res) {
                    resolve(newAuthData);
                } else {
                    const { accessToken } = res;
                    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
                    // setting currentUserId
                    const userGuid = getCurrentUserId(res);
                    const decodedToken = authClient.token.decode(accessToken);
                    const expirationDate = new Date(decodedToken.payload.exp * 1000);
                    newAuthData = {
                        ...newAuthData,
                        authProvider: 'okta',
                        token: accessToken,
                        expTime: expirationDate,
                        tokenType: 'accessToken',
                        userGuid,
                    };
                    setUserId(userGuid);
                    gaDataLayerEvent(gtmEventName.C_Auth_Successful, userGuid);
                    store.commit(`auth/${storeMutation.setAuthData}`, newAuthData);
                    store.commit(`auth/${storeMutation.setCurrentUserId}`, userGuid);
                    store.commit(`memberProfile/${storeMutation.setCurrentUserId}`, userGuid);
                    store.commit(`auth/${storeMutation.setLoggedUser}`, 'user authenticated!');
                    // fetching permissions
                    store.commit(`${storeMutation.setCoreConfigLoadingState}`, true);
                    await Promise.all([
                        store.dispatch(`auth/${storeAction.getPermissions}`, userGuid),
                        store.dispatch(`connectPlus/${storeAction.getConnectPlusRedirects}`),
                    ]);
                    await store.dispatch(`myAccount/${storeAction.getProfileInformation}`, newAuthData.userGuid);
                    await store.dispatch(`myAccount/${storeAction.setDataDogUserInfo}`);
                    store.commit(`${storeMutation.setCoreConfigLoadingState}`, false);
                    store.dispatch(`auth/${storeAction.setSessionInState}`).then(() => {});
                    store.dispatch(`auth/${storeAction.startOktaTokenRefreshTimer}`).then(() => {});
                    resolve(newAuthData);
                }
        });
    });
};

const setTokensForAuthClient = async (tokenData) => {
    authClient.tokenManager.setTokens(tokenData.tokens);
    const isMobileApp = await store.getters[storeGetters.isMobileAppBrowser];
    const storedPostLoginUrl = sessionStorage.getItem(appConstant.storageKeys.ypoForwardToPostLogin);
    if (isMobileApp && (!storedPostLoginUrl || (storedPostLoginUrl && !storedPostLoginUrl.includes("pwreset")))) {
        const messageData = {
            messageType: 'oktaAuthTokens',
            meta: {
                accessToken: tokenData.tokens.accessToken.accessToken,
                idToken: tokenData.tokens.idToken.idToken,
                refreshToken: tokenData.tokens.refreshToken.refreshToken,
            },
        };
        await store.dispatch(`auth/${storeAction.sendMobileMessage}`, window && window.appInterface ? JSON.stringify(messageData) : messageData);
    }
};

async function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
            console.log('Service Worker registered with scope:', registration.scope);
            return registration;
        } catch (error) {
            console.error('Service Worker registration failed:', error);
            throw error;
        }
    } else {
        console.error('Service Workers are not supported by this browser.');
    }
}

const handleFCMTokenManagement = async (userId) => {
    try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
            const registration = await registerServiceWorker();
            const swRegistration = await navigator.serviceWorker.ready;
            if (swRegistration.active) {
                const messaging = getMessaging();
                await getToken(messaging, { vapidKey: process.env.VUE_APP_FIREBASE_PUBLIC_VAPID_KEY, serviceWorkerRegistration: registration })
                    .then((currentToken) => {
                        if (currentToken) {
                            console.log('FCM Token:', currentToken);
                            const tokenObject = {
                                PersonId: userId,
                                FcmToken: currentToken,
                                DeviceModel: 'web',
                            };
                            store.dispatch(`notification/${storeAction.saveFCMToken}`, tokenObject);
                        } else {
                            console.warn('No FCM token available.');
                        }
                    })
                    .catch((err) => {
                        console.error('An error occurred while retrieving the token:', err);
                    });
            }
        } else {
            console.warn('Notification permission was not granted.');
        }
    } catch (error) {
        console.error('Error during FCM token management:', error);
    }
};

// Upon initial site load, we want to check to see if we have our access token. If not check for an okta session to see if we can get one.
const initialSessionCheck = new Promise(async (resolve) => {
    let authData = getDefaultAuthData();
    if (authClient.isLoginRedirect()) {
        const paresTokenDataFromUrl = await authClient.token.parseFromUrl();
        setTokensForAuthClient(paresTokenDataFromUrl);
        await sessionStorage.removeItem(appConstant.storageKeys.loginRedirectModeTriggered);
        authData = await initiateOktaFlow(authData);
        gaDataLayerEvent(gtmEventName.C_Auth_After_Login_Page, authData.userGuid);
        const currentId = getCurrentUserId(paresTokenDataFromUrl.tokens.accessToken);
        if (localStorage.getItem(appConstant.DEFAULT_IS_FIREBASE_SUPPORTED) === appConstant.DEFAULT_FIREBASE_SUPPORTED_TRUE) {
            handleFCMTokenManagement(currentId);
        }
        resolve(authData);
    } else {
        const oktaSessionExists = await authClient.session.exists();
        setPostLoginUrl(window.location.href);
        if (!oktaSessionExists) {
            authClient.tokenManager.remove('accessToken');
            resolve(authData);
        } else {
            const existingSessionTokens = await authClient.token.getWithoutPrompt();
            setTokensForAuthClient(existingSessionTokens);
            authData = await initiateOktaFlow(authData);
            gaDataLayerEvent(gtmEventName.C_Auth_Without_Login_Page, authData.userGuid);
            resolve(authData);
        }
    }
});

const ifAuthenticated = (to, from, next) => {
    Promise.resolve(initialSessionCheck)
        .then(async () => {
            store.commit(storeMutation.handleParamConfig, to.query);
            store.commit(`auth/${storeMutation.checkCookiePolicyAcceptance}`);
            const authData = store.getters[`auth/${storeGetters.authData}`];
            const {
                authProvider,
                token,
                userGuid,
                expTime,
            } = authData;
            const authValid = expTime > new Date();
            if (authProvider) {
                if (to.name === routerNames.SPEAKER) {
                    await handleSpeakerPageNavigation(userGuid, next);
                } else if (authProvider === 'okta' && (!token || !authValid)) {
                    authClient.tokenManager.remove('accessToken');
                    delete axios.defaults.headers.common['X-Connecting-As'];
                    await store.dispatch(`auth/${storeAction.initiateLoginRedirect}`);
                    next(false);
                }

                if (token) {
                    const storedPostLoginUrl = sessionStorage.getItem(appConstant.storageKeys.ypoForwardToPostLogin);
                    if (storedPostLoginUrl) {
                        sessionStorage.removeItem(appConstant.storageKeys.ypoForwardToPostLogin);
                    }
                    const isYngMember = store.getters[`auth/${storeGetters.isYngMember}`];
                    const shouldDefaultToChapterStudioPage = store.getters[`auth/${storeGetters.shouldDefaultToChapterStudioPage}`];
                    const isMemberProspect = store.getters[`auth/${storeGetters.isMemberProspect}`];
                    const isPendingResignation = store.getters[`auth/${storeGetters.isPendingResignation}`];
                    const hasConnectAccess = store.getters[`auth/${storeGetters.hasConnectAccess}`];
                    const isSpeakerView = store.getters[`auth/${storeGetters.isSpeakerView}`];
                    const allowedSpeakerRoutes = [routerNames.SPEAKERPROFILE, routerNames.EDIT_SPEAKER_PROFILE];
                    const userCommunities = store.getters[`chapter/${storeGetters.usersCommunitiesList}`];
                    const primaryCommunity = userCommunities.find(obj => obj.isPrimaryCommunity === true && obj.communityType === 'Chapter');

                    if (isYngMember) {
                        window.open(process.env.VUE_APP_YNG_REDIRECT_URL || '', '_self');
                        return next(false);
                    } else if (!isSpeakerView && !hasConnectAccess) {
                        return next('/errorPage');
                    } else if (token && !(isMemberProspect || isPendingResignation)) {
                        if (storedPostLoginUrl) {
                            if (storedPostLoginUrl.includes('fromURI')) {
                                const urlParams = new URLSearchParams(storedPostLoginUrl.split('?')[1]);
                                const redirectUrl = urlParams.get('fromURI');
                                if (redirectUrl) {
                                    if (redirectUrl.indexOf("isLMS") >= 0) {
                                        redirectFromConnect(redirectUrl, next);
                                    } else { window.open(redirectUrl, '_self'); }
                                }
                            } else if (storedPostLoginUrl.includes('portal')
                                && storedPostLoginUrl.split('portal')[0].includes('#')) {
                                next(false);
                                rewriteProfileLocations();
                            } else if (storedPostLoginUrl.includes('memberProfile') && !storedPostLoginUrl.includes('memberId')) {
                                next(false);
                                memberProfileUrlRedirection();
                            } else if (storedPostLoginUrl.includes('myProfile')) {
                                next(false);
                                memberProfileUrlRedirection(true);
                            } else if (isBaseLandingUrl(storedPostLoginUrl) && shouldDefaultToChapterStudioPage) {
                                next('/chapterStudio');
                            } else if (isBaseLandingUrl(storedPostLoginUrl) && primaryCommunity) {
                                return next({
                                  name: "chapter",
                                  params: {
                                    communityId: primaryCommunity.communityId,
                                  },
                                });
                              }
                             else {
                                const nextRoute = getNextRouteFromUrl(storedPostLoginUrl);
                                return next(nextRoute);
                            }
                        } else if (isSpeakerView && !allowedSpeakerRoutes.includes(to.name)) {
                            return next(`/speakerPage/speakerProfile?id=${store.state.auth.currentUserId}`);
                        } else if (window.location.href.includes('portal')
                            && !window.location.href.split('portal')[0].includes('#')) {
                            next(false);
                            rewriteProfileLocations();
                        } else if (to.name === routerNames.MYPROFILE) {
                            next(false);
                            memberProfileUrlRedirection(true);
                        } else if (to.name === routerNames.MEMBERPROFILE && !(to.query && to.query.memberId)) {
                            next(false);
                            memberProfileUrlRedirection();
                        } else if (to.name === routerNames.YPO_EVENTS) {
                            await handleYpoEventsNavigation(next);
                        } else if (to.name === routerNames.EVENT_DIRECTORY) {
                            await handleEventDirectoryNavigation(userGuid, next);
                        } else if (to.name === 'download') {
                            const downloadResponse = await store.dispatch(`chapter/${storeAction.getDownloadFile}`, {
                                id: to.params.id,
                            });
                            return next(`/${downloadResponse.data.communityType.toLowerCase()}/${downloadResponse.data.communityId}`);
                        } else if (to.name === routerNames.CORONA_VIRUS) {
                            window.location.replace('https://connect.ypowpo.org/portal/department/about-ypo/coronavirus-update/coronavirus-update');
                        } else if (to.name.toLowerCase() === routerNames.BUSINESSES.toLowerCase()) {
                            await store.dispatch(`chapter/${storeAction.getBusinessCommunityId}`);
                            return next();
                        } else if (routesToNavigateToHivebrite.includes(to.name.toLowerCase())) {
                            const { communityId } = to.params;
                            if (communityId) {
                                let hbCommunities = store.getters[`connectRedirect/${storeGetters.connectPlusRedirects}`];
                                if (!hbCommunities) {
                                    hbCommunities = await store.dispatch(`connectPlus/${storeAction.getConnectPlusRedirects}`);
                                }
                                const hbRedirectCommunity = getHbRedirectByCommunityId(hbCommunities, communityId);
                                if (hbRedirectCommunity) {
                                    window.open(hbRedirectCommunity.redirectUrl, '_self');
                                    next(false);
                                }
                            }
                            return next();
                        } else if (to.name === routerNames.FOLLOWS) {
                            return next('/memberConnections');
                        } else if (to.name === routerNames.CHAPTER_STUDIO) {
                            const canViewChapterStudioPage = store.getters[`auth/${storeGetters.canViewChapterStudioPage}`];
                            if (!canViewChapterStudioPage) return next('/home');
                            return next();
                        } else if (to.name === routerNames.MANAGE_CHAPTER_STUDIO || to.name === routerNames.CREATE_ANNOUNCEMENT) {
                            const canViewChapterStudioPage = store.getters[`auth/${storeGetters.canViewManageChapterStudioPage}`];
                            if (canViewChapterStudioPage) {
                                store.commit(`manageChapterStudio/${storeMutation.setBreadcrumbList}`, {
                                    toRoute: to,
                                    fromRoute: from,
                                });
                                return next();
                            }
                            return next('/home');
                        } else {
                            return next();
                        }
                    } else if (isMemberProspect || isPendingResignation) {
                        window.open(paymentRedirectUrl, '_self');
                    } else if (!token && to.name === routerNames.SPEAKER) {
                        return next();
                    } else {
                        return next('/home');
                    }
                }
            }
        });
};

const router = new VueRouter({
    //TODO: handle server setting to add this later
    //mode: 'history', //removes # from urls so that application routes can be defined server-side
    routes: [
        {
            path: '/',
            name: 'Layout', //TODO: will change it to home when available
            components: {
                header: routableComponents.header,
                default: routableComponents.layout,
            },
            redirect: '/home',
            children: [
                {
                    path: '/home',
                    name: routerNames.HOME,
                    components: {
                        banner: routableComponents.homeBanner,
                        rightSidebar: routableComponents.homeRightSidebar,
                        leftSidebar: routableComponents.homeLeftSidebar,
                        default: routableComponents.homePage,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/chapter/:communityId?',
                    name: routerNames.CHAPTER,
                    components: {
                        banner: routableComponents.chapterBanner,
                        rightSidebar: routableComponents.chapterRightSidebar,
                        leftSidebar: routableComponents.chapterLeftSideBar,
                        default: routableComponents.chapterMain,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/community/:communityId?',
                    name: routerNames.CHAMPION,
                    components: {
                        banner: routableComponents.chapterBanner,
                        rightSidebar: routableComponents.chapterRightSidebar,
                        leftSidebar: routableComponents.chapterLeftSideBar,
                        default: routableComponents.championMain,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/forum/:communityId?',
                    name: routerNames.FORUM,
                    components: {
                        banner: routableComponents.chapterBanner,
                        rightSidebar: routableComponents.forumRightSidebar,
                        leftSidebar: routableComponents.forumLeftSideBar,
                        default: routableComponents.chapterPage,
                    },
                    beforeEnter: ifAuthenticated,
                    children: [
                        {
                            path: '/forum/exercise/:type?',
                            name: routerNames.FORUM_EXERCISE,
                            components: {
                                banner: routableComponents.chapterBanner,
                                rightSidebar: routableComponents.forumRightSidebar,
                                leftSidebar: routableComponents.forumLeftSideBar,
                                default: routableComponents.chapterPage,
                            },
                            beforeEnter: ifAuthenticated,
                        },
                    ],
                },
                {
                    path: '/region/:communityId?',
                    name: routerNames.REGION,
                    components: {
                        banner: routableComponents.chapterBanner,
                        rightSidebar: routableComponents.regionRightSidebar,
                        leftSidebar: routableComponents.regionLeftSidebar,
                        default: routableComponents.chapterPage,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/network/:communityId?',
                    name: routerNames.NETWORK,
                    components: {
                        banner: routableComponents.networkBanner,
                        rightSidebar: routableComponents.networkRightSidebar,
                        leftSidebar: routableComponents.networkLeftSidebar,
                        default: routableComponents.networkMain,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/communities/:communityId?',
                    name: routerNames.COMMUNITIES,
                    components: {
                        header: routableComponents.header,
                        banner: routableComponents.communityBanner,
                        rightSidebar: routableComponents.communityRightSideBar,
                        leftSidebar: routableComponents.communitiesLeftSidebar,
                        default: routableComponents.communities,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/group/:communityId?',
                    name: routerNames.GROUP,
                    components: {
                        banner: routableComponents.groupBanner,
                        rightSidebar: routableComponents.groupRightSideBar,
                        leftSidebar: routableComponents.groupLeftSideBar,
                        default: routableComponents.groupMain,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/businesses',
                    name: routerNames.BUSINESSES,
                    components: {
                        banner: routableComponents.businessesBanner,
                        leftSidebar: routableComponents.businessesLeftSidebar,
                        default: routableComponents.businesses,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: '/mentoring',
                    redirect: '/group/14892960-07b2-4f33-9c4b-ccceedd09266',
                },
                {
                    path: '/championmentoring',
                    redirect: '/group/fd8485bd-9ba1-4124-8ae4-746229c84280',
                },
                {
                    path: '/executiveeducation',
                    redirect: '/group/fcf1c458-7ec4-4456-9306-dcb9dd9862ff',
                },
            ],
        },
        {
            path: '/speakerPage',
            name: routerNames.RESOURCEDIRECTORY,
            components: {
                header: routableComponents.header,
                default: routableComponents.resourceDirectoryMain,
            },
            beforeEnter: ifAuthenticated,
            children: [
                {
                    path: 'speakerProfile',
                    name: routerNames.SPEAKERPROFILE,
                    components: {
                        banner: routableComponents.speakerBanner,
                        default: routableComponents.speakerProfile,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: 'speakerAdministration',
                    name: routerNames.SPEAKER_ADMINSTRATION,
                    beforeEnter(to, from, next) {
                        window.location.href = appConstant.SPEAKER_RESOURCE_DIRECTORY;
                    },
                },
                {
                    path: '/download/:id',
                    name: 'download',
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: 'searchSpeakers',
                    name: routerNames.SEARCH_SPEAKERS,
                    components: {
                        default: routableComponents.searchSpeakers,
                    },
                    beforeEnter(to, from, next) {
                        const { query } = to;
                        if (query && query.speakersWhoAre === 'IsCFFSpeaker') {
                            ifAuthenticated(to, from, next);
                        } else {
                            window.location.href = appConstant.SPEAKER_RESOURCE_DIRECTORY;
                        }
                    },
                },
                {
                    path: 'editSpeakerProfile',
                    name: routerNames.EDIT_SPEAKER_PROFILE,
                    components: {
                        default: routableComponents.editSpeakerProfile,
                    },
                    beforeEnter: ifAuthenticated,
                },
            ],
        },
        {
            path: '/loginSecurity',
            name: routerNames.MANAGELOGINSECURITY,
            components: {
                header: routableComponents.header,
                default: routableComponents.manageLoginSecurity,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/privacySettings',
            name: routerNames.PRIVACYSETTINGS,
            components: {
                header: routableComponents.header,
                default: routableComponents.privacySettings,
            },
            beforeEnter: ifAuthenticated,
            children: [
                {
                    path: 'emailSettings',
                    name: routerNames.EMAILSETTINGS,
                    components: {
                        default: routableComponents.emailSettings,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: 'notification',
                    name: routerNames.NOTIFICATIONS,
                    components: {
                        default: routableComponents.notification,
                    },
                    beforeEnter: ifAuthenticated,
                },
                {
                    path: 'visibility',
                    name: routerNames.VISIBILITY,
                    components: {
                        default: routableComponents.myVisibility,
                    },
                    beforeEnter: ifAuthenticated,
                },
            ],
        },
        {
            path: '/myProfile',
            name: routerNames.MYPROFILE,
            components: {
                header: routableComponents.header,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/memberProfile',
            name: routerNames.MEMBERPROFILE,
            components: {
                header: routableComponents.header,
                default: routableComponents.memberProfile,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/forgotusername',
            name: routerNames.FORGOT_USER_NAME,
            components: {
                default: routableComponents.login,
            },
        },
        {
            path: '/memberDirectoryPage',
            name: routerNames.MEMBER_DIRECTORY_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.memberDirectoryPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/events',
            name: routerNames.YPO_EVENTS,
            components: {
                header: routableComponents.header,
                default: routableComponents.ypoEvents,
            },
           beforeEnter: ifAuthenticated,
        },
        {
            path: '/eventDirectoryPage',
            name: routerNames.EVENT_DIRECTORY_PAGE,
            redirect: () => {
                return {
                    path: '/eventDirectory',
                };
            },
        },
        {
            path: '/eventDirectory',
            name: routerNames.EVENT_DIRECTORY,
            components: {
                header: routableComponents.header,
                default: routableComponents.ypoEvents,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/contentDirectoryPage',
            name: routerNames.CONTENT_DIRECTORY_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.contentDirectoryPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/communityDirectoryPage',
            name: routerNames.COMMUNITY_DIRECTORY_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.communityDirectoryPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/allNetworksPage',
            name: routerNames.ALL_NETWORKS_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.allNetworksPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/networkLandingPage/:communityId?',
            name: routerNames.NETWORK_LANDING_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.networkLandingPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/memberConnections',
            name: routerNames.MEMBER_CONNECTIONS,
            components: {
                header: routableComponents.header,
                default: routableComponents.memberConnectionPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/follows',
            name: routerNames.FOLLOWS,
            components: {
                header: routableComponents.header,
                default: routableComponents.memberConnectionPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/speakers',
            name: routerNames.SPEAKER,
            beforeEnter(to, from, next) {
                window.location.href = appConstant.SPEAKER_REGISTRATION;
            },
        },
        {
            path: '/messenger',
            name: routerNames.MESSENGER,
            components: {
                header: routableComponents.header,
                default: routableComponents.messengerPage,
            },
            props: {
                default: route => ({
                    ...route.query,
                    ...route.params,
                }),
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/version',
            name: routerNames.APP_VERSION,
            components: {
                default: routableComponents.appVersion,
            },
        },
        {
            path: '/errorPage',
            name: routerNames.ERROR_PAGE,
            components: {
                default: routableComponents.errorPage,
            },
        },
        {
            path: '/assistPage',
            name: routerNames.ASSISTPAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.assistPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/chapter-event/:communityId',
            name: routerNames.CHAPTER_EVENT,
            components: {
                header: routableComponents.header,
                default: routableComponents.chapterEvent,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/chapterWaiver/:communityId?',
            name: routerNames.CHAPTER_WAIVER,
            components: {
                header: routableComponents.header,
                default: routableComponents.chapterWaiver,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/peerReviewPanelAdmin',
            name: routerNames.PEER_REVIEW_PANEL_ADMIN,
            components: {
                header: routableComponents.header,
                default: routableComponents.peerReviewPanelAdmin,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/peerReviewPanel',
            name: routerNames.PEER_REVIEW_PANEL,
            components: {
                header: routableComponents.header,
                default: routableComponents.peerReviewPanel,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/regionalWaiver/:communityId?',
            name: routerNames.REGIONAL_MEMBER_OFFICER,
            components: {
                header: routableComponents.header,
                default: routableComponents.regionalMemberOfficer,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/eventAttendees/:eventId?',
            name: routerNames.EVENT_ATTENDEES,
            component: routableComponents.eventAttendees,
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/waiverForm',
            name: routerNames.WAIVER_FORM,
            components: {
                default: routableComponents.waiverForm,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/waiverFormCommon/:viewType?',
            name: routerNames.WAIVER_FORM_COMMON,
            components: {
                default: routableComponents.waiverFormCommon,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/coronavirus',
            name: routerNames.CORONA_VIRUS,
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/waiverFormPRPAdmin',
            name: routerNames.WAIVER_FORM_PRP_ADMIN,
            components: {
                default: routableComponents.waiverFormPRPAdmin,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/concierge',
            name: routerNames.CONCIERGE_PAGE,
            redirect: () => {
                return {
                    path: `/authConnect/${routerNames.CONCIERGE_PAGE}`,
                };
            },
        },
        {
            path: '/lms',
            name: routerNames.LMS_PAGE,
            redirect: () => {
                return {
                    path: `/authConnect/${routerNames.LMS_PAGE}`,
                };
            },
        },
        {
            path: '/chaptermanagement',
            name: routerNames.CHAPTER_MANAGEMENT,
            redirect: () => {
                return {
                    path: `/authConnect/${routerNames.CHAPTER_MANAGEMENT}`,
                };
            },
        },
        {
            path: '/m2mxprofile',
            name: routerNames.M2MX_PROFILE,
            redirect: () => {
                return {
                    path: `/authConnect/${routerNames.M2MX_PROFILE}`,
                };
            },
        },
        {
            path: '/authConnect/:forwardFrom?',
            name: routerNames.AUTH_WITH_CONNECT,
            components: {
                default: routableComponents.authenticateAndNavigate,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/resources',
            name: routerNames.RESOURCE_MENU_PAGE,
            components: {
                header: routableComponents.header,
                default: routableComponents.resourceMenuPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/chapterStudio',
            name: routerNames.CHAPTER_STUDIO,
            components: {
                header: routableComponents.header,
                default: routableComponents.chapterStudioPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/manageAnnouncements',
            name: routerNames.MANAGE_CHAPTER_STUDIO,
            components: {
                header: routableComponents.header,
                default: routableComponents.manageChapterStudioPage,
            },
            beforeEnter: ifAuthenticated,
        },
        {
            path: '/createAnnouncement/:announcementId?',
            name: routerNames.CREATE_ANNOUNCEMENT,
            components: {
                header: routableComponents.header,
                default: routableComponents.createChapterStudioAnnouncement,
            },
            props: route => ({ announcementId: route.query.announcementId }),
            beforeEnter: ifAuthenticated,
        },
        {
            path: '*',
            redirect: '/',
        },
    ],
    scrollBehavior: () => {
        return { x: 0, y: 0 };
    },
});

const searchPath = [
    routerNames.YPO_EVENTS,
    routerNames.MEMBER_DIRECTORY_PAGE,
    routerNames.COMMUNITY_DIRECTORY_PAGE,
    routerNames.CONTENT_DIRECTORY_PAGE,
]

const excludeBlankFilters =  (path, query,filterKey) => {
    const q = { ...query };
    const filters = JSON.parse(q[filterKey]);
    try {
        if (filters && typeof filters === 'object') {
            Object.keys(filters).forEach(k => {
                if (!Array.isArray(filters[k]) || filters[k].length === 0) {
                    delete filters[k];
                }
            });
            if (Object.keys(filters).length) {
                q[filterKey] = JSON.stringify(filters);
            } else {
                delete q[filterKey];
            }
        }
    } catch (error) {
        console.error('Error during excludeBlankFilters route', error);
    }

    const qs = new URLSearchParams(q).toString();
    return qs ? `${path}?${qs}` : path;
};

router.afterEach((to) => {
    store.state.routeParams = { ...to.params };
    store.state.routeName = to.name;
    store.state.nextActivityFeedListSearchOffset = 0;
    store.dispatch(`messenger/${storeAction.ensureMessengerSession}`);
    let fullPath = to.fullPath;
    const filterKey = Object
        .keys(to.query)
        .find(key => key.toLowerCase().includes('filter')
            && typeof to.query[key] === 'string');
    if (searchPath.includes(store.state.routeName) && to.query && filterKey) {
        fullPath = excludeBlankFilters(to.path, to.query, filterKey);
    }

    gaPageViewEvent(gtmEventName.C_Page_View, fullPath, `Connect - ${capitalizedString(to.name)}`);
    datadogRum.setViewName(`/${capitalizedString(to.name)}`);
});

router.beforeEach((to, from, next) => {
    const refId = to.query && to.query.RefID ? to.query.RefID : null;
    const ypoAttributionData = JSON.parse(sessionStorage.getItem(appConstant.storageKeys.ypoAttributionData));
    function setAttribution() {
        sessionStorage.setItem(appConstant.storageKeys.ypoAttributionData, JSON.stringify({
            refId,
            expiresAt: addDays(new Date(), 1),
        }));
    }
    if (ypoAttributionData) {
        if (refId && ypoAttributionData.refId !== refId) {
            setAttribution();
        } else if (new Date(ypoAttributionData.expiresAt) <= new Date()) {
            sessionStorage.removeItem(appConstant.storageKeys.ypoAttributionData);
        }
    } else if (refId) {
       setAttribution();
    }
    next();
});

export default router;
